'use client';

import { Card, ErrorMessage, LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useLogin } from '@/lib/hooks/useAuthMutations';
import { googleAuth } from '@/lib/api/auth';
import { cssVars } from '@/styles/theme';
import Logo from '@/components/shared/misc/Logo';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Shield,
  Sparkles,
  User,
} from 'lucide-react';
import { getRedirectPathAfterLogin } from '@/lib/utils/authRedirect';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Script from 'next/script';
import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

// TypeScript declaration for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              type?: 'standard' | 'icon';
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
              width?: number;
              locale?: string;
            }
          ) => void;
          prompt: (callback?: (notification: PromptMomentNotification) => void) => void;
          disableAutoSelect: () => void;
          revoke: (hint: string, callback?: () => void) => void;
        };
      };
    };
  }
}

interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
  clientId?: string;
}

interface PromptMomentNotification {
  isDisplayMoment: () => boolean;
  isDisplayed: () => boolean;
  isNotDisplayed: () => boolean;
  getNotDisplayedReason: () => string;
  isSkippedMoment: () => boolean;
  getSkippedReason: () => string;
  isDismissedMoment: () => boolean;
  getDismissedReason: () => string;
}

export default function LoginForm() {
  const t = useTranslations('auth');
  const locale = useLocale();
  const router = useRouter();
  const isRTL = locale === 'ar';
  const { setAuthFromResponse } = useAuth();
  const { login, isLoading, error, clearError } = useLogin();
  const [identifier, setIdentifier] = useState(''); // email or phone
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Google Sign-In states
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Detect if identifier is a phone number or email
  const isPhoneNumber = (value: string) => {
    // Check if it starts with + or is all digits (possibly with spaces/dashes)
    const cleaned = value.replace(/[\s\-\(\)]/g, '');
    return /^[\+]?[0-9]{8,15}$/.test(cleaned) || /^[0-9]+$/.test(cleaned);
  };

  /**
   * Handle Google Sign-In response
   */
  const handleGoogleResponse = useCallback(
    async (response: GoogleCredentialResponse) => {
      const idToken = response.credential;

      if (!idToken) {
        toast.error(t('login.errors.googleTokenMissing') || 'Google token missing');
        return;
      }

      setIsGoogleLoading(true);

      try {
        // Send ID token to backend for verification and login
        // intent: 'login' = only existing users; new users get AUTH_NEW_USER_NEEDS_REGISTRATION and redirect to register
        const authResponse = await googleAuth({
          idToken,
          userType: 'client',
          intent: 'login',
          locale,
        });

        // Update auth state
        const authSet = setAuthFromResponse(authResponse);
        if (!authSet) {
          console.warn('Auth state was not set from Google login response');
        }

        // Redirect: return to saved page if token expired, else dashboard
        const userType = authResponse.user?.userType;
        const defaultPath =
          userType === 'admin' ? `/${locale}/admin/dashboard` : `/${locale}/dashboard`;
        const redirectPath = getRedirectPathAfterLogin(defaultPath);
        router.push(redirectPath);
        router.refresh();
      } catch (err: unknown) {
        console.error('Google login error:', err);
        const errorMessage = err instanceof Error ? err.message : '';
        
        if (errorMessage.includes('AUTH_NEW_USER_NEEDS_REGISTRATION')) {
          // لا يوجد حساب مرتبط بـ Google — التسجيل الجديد عبر Google غير متاح، التوجيه للتسجيل بالبريد/الجوال
          toast.info(t('login.errors.newUserRegisterWithEmail') || 'لا يوجد حساب مرتبط بحساب Google. يرجى التسجيل باستخدام البريد أو الجوال.');
          router.push(`/${locale}/register`);
          router.refresh();
        } else if (errorMessage.includes('AUTH_GOOGLE_TOKEN_INVALID')) {
          toast.error(t('login.errors.googleTokenMissing') || 'Invalid Google token');
        } else if (errorMessage.includes('AUTH_ACCOUNT_SUSPENDED')) {
          toast.error(t('login.errors.accountSuspended') || 'Account suspended');
        } else if (errorMessage.includes('AUTH_GOOGLE_NOT_CONFIGURED')) {
          toast.error(t('login.errors.oauthNotConfigured') || 'Google OAuth not configured');
        } else {
          toast.error(errorMessage || t('login.errors.googleFailed') || 'Google login failed');
        }
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [locale, router, setAuthFromResponse, t]
  );

  /**
   * Initialize Google Identity Services when script loads.
   * Retries a few times because the script load event can fire before
   * window.google.accounts.id is actually available.
   */
  const initializeGoogleSignIn = useCallback(() => {
    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

    if (!clientId) {
      console.warn('Google Client ID not configured');
      return;
    }

    const tryInit = (attempt = 0) => {
      const maxAttempts = 10;
      const delayMs = 100;

      if (!window.google?.accounts?.id) {
        if (attempt < maxAttempts) {
          setTimeout(() => tryInit(attempt + 1), delayMs);
          return;
        }
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        setIsGoogleScriptLoaded(true);
      } catch (err) {
        console.error('Failed to initialize Google Sign-In:', err);
      }
    };

    tryInit(0);
  }, [handleGoogleResponse]);

  /**
   * Render Google Sign-In button
   */
  useEffect(() => {
    if (isGoogleScriptLoaded && googleButtonRef.current) {
      const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
      if (!clientId || !window.google?.accounts?.id) return;

      // Clear previous button content
      googleButtonRef.current.innerHTML = '';

      // Render the Google Sign-In button with icon only style
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'icon',
        theme: 'outline',
        size: 'large',
        shape: 'circle',
        locale: locale === 'ar' ? 'ar' : 'en',
      });
    }
  }, [isGoogleScriptLoaded, locale]);

  // Clear error when form is submitted (not when typing)
  // This allows errors to be visible until user tries again

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // Prevent default form submission and page reload
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple submissions
    if (isLoading) {
      console.log('⏸️ LoginForm: Already loading, preventing duplicate submission');
      return;
    }

    // Track if login was successful - only redirect if this is true
    let loginSuccessful = false;

    try {
      console.log('🟢 LoginForm: Starting login process...');

      // Step 1: Call login API and wait for response
      // Detect if identifier is phone or email
      const response = isPhoneNumber(identifier)
        ? await login({ phone: identifier, password })
        : await login({ email: identifier, password });

      console.log('✅ LoginForm: Login API call successful');

      // Step 2: Validate response before proceeding
      if (!response || !response.user || !response.token) {
        console.error('🔴 LoginForm: Invalid response structure:', response);
        throw new Error(t('login.errors.invalidResponse'));
      }

      // Step 3: Update auth state from response and verify it succeeded
      const authUpdated = setAuthFromResponse?.(response);
      if (!authUpdated) {
        console.error('🔴 LoginForm: Failed to update auth state');
        throw new Error(t('login.errors.updateAuthFailed'));
      }

      // Step 4: Verify token was saved correctly
      const savedToken = localStorage.getItem('token');
      if (!savedToken || savedToken !== response.token) {
        console.error('🔴 LoginForm: Token verification failed', {
          saved: savedToken,
          expected: response.token,
        });
        throw new Error(t('login.errors.saveAuthFailed'));
      }

      console.log('✅ LoginForm: All validations passed');

      // Step 5: Mark login as successful only after all validations pass
      loginSuccessful = true;

      // Step 6: Redirect - return to saved page if token expired, else dashboard
      if (loginSuccessful) {
        const userType = response.user.userType;
        const defaultPath =
          userType === 'admin' ? `/${locale}/admin/dashboard` : `/${locale}/dashboard`;
        const redirectPath = getRedirectPathAfterLogin(defaultPath);
        console.log('🔄 LoginForm: Redirecting to:', redirectPath);
        router.push(redirectPath);
      }
    } catch (err) {
      // Error is already set in the hook via setError()
      // The error will be displayed in the UI
      console.error('🔴 LoginForm: Login failed, error caught:', {
        error: err,
        errorMessage: err instanceof Error ? err.message : String(err),
        errorInState: error,
        errorType: err instanceof Error ? err.constructor.name : typeof err,
        loginSuccessful, // Should always be false here
      });

      // Ensure we stay on login page - do NOT redirect on error
      // User will see the error message and can try again
      // Explicitly prevent any navigation
      loginSuccessful = false;

      // Don't do anything else - just let the error display
      // The form will remain on the page and show the error message
      return;
    }
  };

  return (
    <>
      {/* Google Identity Services Script */}
      <Script
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive"
        onLoad={initializeGoogleSignIn}
      />

      <Card className="w-full max-w-md p-4 sm:p-6 md:p-8">
        {/* Header */}
      <div className="mb-6 text-center sm:mb-8">
        {/* Brand Logo */}
        <div className="mb-4 flex justify-center sm:mb-6">
          <Logo width={64} height={64} showText={false} withName={true} />
        </div>

        <div
          className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 sm:mb-4 sm:px-4 sm:py-2"
          style={{
            backgroundColor: `color-mix(in srgb, ${cssVars.primary.DEFAULT} 10%, transparent)`,
            border: `1px solid ${cssVars.primary.DEFAULT}30`,
          }}
        >
          <Sparkles
            className="h-3.5 w-3.5 sm:h-4 sm:w-4"
            style={{ color: cssVars.primary.DEFAULT }}
          />
          <span className="text-xs font-bold sm:text-sm" style={{ color: cssVars.primary.DEFAULT }}>
            {t('login.badge')}
          </span>
        </div>

        <h1
          className="mb-2 text-2xl font-bold sm:text-3xl md:text-4xl"
          style={{ color: cssVars.secondary.DEFAULT }}
        >
          {t('login.title')}
        </h1>
        <p className="text-sm sm:text-base" style={{ color: cssVars.neutral.textSecondary }}>
          {t('login.subtitle')}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <motion.div
          key={error.message || 'error'} // Force re-render when error changes
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mb-6"
        >
          <ErrorMessage error={error} variant="inline" onDismiss={clearError} />
        </motion.div>
      )}

      {/* Quick Login Buttons (Development Only) */}
      {typeof window !== 'undefined' && process.env.NODE_ENV === 'development' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-lg border-2 p-4 sm:mb-8 sm:p-5"
          style={{
            backgroundColor: `color-mix(in srgb, ${cssVars.primary.DEFAULT} 5%, transparent)`,
            borderColor: cssVars.primary.DEFAULT + '40',
          }}
        >
          <div className="mb-3 text-center">
            <span
              className="text-xs font-bold sm:text-sm"
              style={{ color: cssVars.primary.DEFAULT }}
            >
              {t('devMode.quickLogin.title')}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-4">
            {[
              {
                label: t('devMode.quickLogin.userTypes.client'),
                email: 'client@test.com',
                password: 'Client@123',
                icon: User,
                color: cssVars.primary.DEFAULT,
              },
              {
                label: t('devMode.quickLogin.userTypes.supplier'),
                email: 'supplier@test.com',
                password: 'Supplier@123',
                icon: Building2,
                color: cssVars.primary.DEFAULT,
              },
              {
                label: t('devMode.quickLogin.userTypes.admin'),
                email: 'admin@erssa.com',
                password: 'Admin@123',
                icon: Shield,
                color: cssVars.primary.DEFAULT,
              },
              {
                label: t('devMode.quickLogin.users.hamadClient'),
                email: 'hamad@alharbi.com',
                password: '11223344',
                icon: User,
                color: cssVars.primary.DEFAULT,
              },
              {
                label: t('devMode.quickLogin.users.hamadSupplier'),
                email: 'hamad@company.com',
                password: '11223344',
                icon: Building2,
                color: cssVars.primary.DEFAULT,
              },
              {
                label: t('devMode.quickLogin.users.mohammedClient'),
                email: 'mohammed@subaie.com',
                password: '11223344',
                icon: User,
                color: cssVars.primary.DEFAULT,
              },
              {
                label: t('devMode.quickLogin.users.mohammedSupplier'),
                email: 'mohammed@company.com',
                password: '11223344',
                icon: Building2,
                color: cssVars.primary.DEFAULT,
              },
            ].map((user) => {
              const Icon = user.icon;
              const handleQuickLogin = async () => {
                if (isLoading) return;

                setIdentifier(user.email);
                setPassword(user.password);
                clearError();

                // Trigger login
                try {
                  const response = await login({ email: user.email, password: user.password });

                  if (!response || !response.user || !response.token) {
                    throw new Error(t('devMode.quickLogin.errors.invalidResponse'));
                  }

                  const authUpdated = setAuthFromResponse?.(response);
                  if (!authUpdated) {
                    throw new Error(t('devMode.quickLogin.errors.updateAuthFailed'));
                  }

                  const userType = response.user.userType;
                  const defaultPath =
                    userType === 'admin' ? `/${locale}/admin/dashboard` : `/${locale}/dashboard`;
                  const redirectPath = getRedirectPathAfterLogin(defaultPath);
                  router.push(redirectPath);
                } catch (err) {
                  console.error('Quick login failed:', err);
                  // Error will be displayed via the error state from useLogin hook
                }
              };
              return (
                <motion.button
                  key={user.email}
                  type="button"
                  onClick={handleQuickLogin}
                  disabled={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex flex-col items-center gap-2 rounded-lg border-2 py-3 text-xs font-bold transition-all disabled:opacity-50 sm:rounded-xl sm:py-3 sm:text-sm"
                  style={{
                    backgroundColor: cssVars.neutral.bg,
                    borderColor: user.color + '60',
                    color: user.color,
                  }}
                >
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>{user.label}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" noValidate>
        {/* Identifier Field (Email or Phone) */}
        <div>
          <label
            className="mb-1.5 block text-sm font-bold sm:mb-2 sm:text-base"
            style={{ color: cssVars.secondary.DEFAULT }}
          >
            {t('login.identifier')}
          </label>
          <div className="relative">
            <Mail
              className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
              style={{ color: cssVars.neutral.textMuted }}
            />
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              dir="ltr"
              className="w-full rounded-lg border-2 py-2.5 pl-3 pr-10 text-base font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-12"
              style={{
                backgroundColor: cssVars.neutral.bg,
                color: cssVars.secondary.DEFAULT,
                borderColor: cssVars.neutral.border,
              }}
              placeholder={t('login.identifierPlaceholder')}
            />
          </div>
        </div>

        {/* Password Field */}
        <div>
          <label
            className="mb-1.5 block text-sm font-bold sm:mb-2 sm:text-base"
            style={{ color: cssVars.secondary.DEFAULT }}
          >
            {t('common.password')}
          </label>
          <div className="relative">
            <Lock
              className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
              style={{ color: cssVars.neutral.textMuted }}
            />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full rounded-lg border-2 py-2.5 pl-10 pr-10 text-base font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-12 sm:pr-12"
              style={{
                backgroundColor: cssVars.neutral.bg,
                color: cssVars.secondary.DEFAULT,
                borderColor: cssVars.neutral.border,
              }}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute start-0 top-1/2 flex min-h-[44px] min-w-[44px] -translate-y-1/2 items-center justify-center transition-all touch-manipulation sm:start-2"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff
                  className="h-4 w-4 sm:h-5 sm:w-5"
                  style={{ color: cssVars.neutral.textMuted }}
                />
              ) : (
                <Eye
                  className="h-4 w-4 sm:h-5 sm:w-5"
                  style={{ color: cssVars.neutral.textMuted }}
                />
              )}
            </button>
          </div>
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded border-2 sm:h-5 sm:w-5"
              style={{ accentColor: cssVars.primary.DEFAULT }}
            />
            <span
              className="text-sm font-semibold sm:text-base"
              style={{ color: cssVars.neutral.textSecondary }}
            >
              {t('common.rememberMe')}
            </span>
          </label>

          <Link
            href={`/${locale}/forgot-password`}
            className="text-sm font-bold transition-all hover:underline sm:text-base"
            style={{ color: cssVars.primary.DEFAULT }}
          >
            {t('common.forgotPassword')}
          </Link>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full items-center justify-center gap-2 rounded-lg py-3 text-base font-bold shadow-lg transition-all disabled:opacity-50 sm:gap-3 sm:rounded-xl sm:py-4 sm:text-lg"
          style={{
            background: cssVars.gradient.primary,
            color: cssVars.neutral.surface,
          }}
        >
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              {t('login.submitButton')}
              {isRTL ? (
                <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </>
          )}
        </button>
      </form>

        {/* Divider with Google Icon */}
        <div className="relative my-6 sm:my-8">
          {/* Lines */}
          <div className="absolute inset-0 flex items-center">
            <div className="w-full" style={{ borderTop: `1px solid ${cssVars.neutral.border}` }} />
          </div>
          
          {/* Google Icon in the middle */}
          <div className="relative flex justify-center">
            {isGoogleLoading ? (
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full sm:h-12 sm:w-12"
                style={{ backgroundColor: cssVars.neutral.surface }}
              >
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <div
                ref={googleButtonRef}
                className="flex items-center justify-center"
                style={{ backgroundColor: cssVars.neutral.surface }}
              >
                {/* Fallback Google button if script not loaded */}
                {!isGoogleScriptLoaded && (
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all sm:h-12 sm:w-12"
                    style={{
                      backgroundColor: cssVars.neutral.surface,
                      borderColor: cssVars.neutral.border,
                    }}
                  >
                    {/* Google Icon SVG */}
                    <svg className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  </motion.button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sign Up Link */}
        <div className="text-center">
          <p className="text-xs sm:text-sm" style={{ color: cssVars.neutral.textSecondary }}>
            {t('common.dontHaveAccount')}{' '}
            <Link
              href={`/${locale}/register`}
              className="font-bold transition-all hover:underline"
              style={{ color: cssVars.primary.DEFAULT }}
            >
              {t('login.signUpLink')}
            </Link>
          </p>
        </div>
      </Card>
    </>
  );
}
